import { useState, useEffect, useRef, useCallback } from 'react';
import { Alert } from 'react-native';
import { api, noCreds, publicApi } from '@api/api';
import SocketManager from '@utils/socketManager';
import WebRTCManager from '@utils/webrtcManager.web';

// WebRTC Configuration
const rtcConfiguration = {
	iceServers: [
		{ urls: 'stun:stun.l.google.com:19302' },
		{ urls: 'stun:stun1.l.google.com:19302' },
	],
};

// Types
export interface CallState {
	callId: string | null;
	isInCall: boolean;
	isIncoming: boolean;
	caller: any;
	callee: any;
	callType: 'video' | 'audio';
	status: 'idle' | 'calling' | 'ringing' | 'connected' | 'ended';
}

export interface MediaState {
	video: boolean;
	audio: boolean;
	screenShare: boolean;
}

export interface ConferenceState {
	roomId: string | null;
	isInConference: boolean;
	participants: any[];
	isHost: boolean;
	roomSettings: {
		maxParticipants: number;
		isLocked: boolean;
		allowScreenShare: boolean;
		allowChat: boolean;
	};
}

export interface ChatState {
	messages: any[];
	newMessage: string;
	isTyping: boolean;
	typingUsers: any[];
}

export interface UseMeetingReturn {
	// States
	callState: CallState;
	mediaState: MediaState;
	conferenceState: ConferenceState;
	chatState: ChatState;
	isLoading: boolean;
	hasMediaPermission: boolean;
	connectionStatus: string;
	webrtcManager: WebRTCManager;
	socketManager: SocketManager;

	// State setters
	setMediaState: React.Dispatch<React.SetStateAction<MediaState>>;
	setHasMediaPermission: React.Dispatch<React.SetStateAction<boolean>>;
	setChatState: React.Dispatch<React.SetStateAction<ChatState>>;

	// Meeting functions
	createConference: (meetingName?: string) => Promise<void>;
	joinConference: (roomId: string) => Promise<void>;
	leaveConference: () => void;

	// Chat functions
	joinMeetingRoom: (userId: string) => void;
	sendMessage: (userId: string) => void;
	handleTyping: (isTyping: boolean, userId: string) => void;
	loadChatHistory: () => void;

	// Media functions
	getLocalMedia: (video?: boolean) => Promise<MediaStream | null>;
	cleanupMedia: () => void;
	initializePeerConnection: (callId: string) => Promise<RTCPeerConnection>;
}

export const useMeeting = (
	platform: 'web' | 'mobile' = 'web'
): UseMeetingReturn => {
	// States
	const [callState, setCallState] = useState<CallState>({
		callId: null,
		isInCall: false,
		isIncoming: false,
		caller: null,
		callee: null,
		callType: 'video',
		status: 'idle',
	});

	const [mediaState, setMediaState] = useState<MediaState>({
		video: true,
		audio: true,
		screenShare: false,
	});

	const [conferenceState, setConferenceState] = useState<ConferenceState>({
		roomId: null,
		isInConference: false,
		participants: [],
		isHost: false,
		roomSettings: {
			maxParticipants: 10,
			isLocked: false,
			allowScreenShare: true,
			allowChat: true,
		},
	});

	const [chatState, setChatState] = useState<ChatState>({
		messages: [],
		newMessage: '',
		isTyping: false,
		typingUsers: [],
	});

	const [isLoading, setIsLoading] = useState(false);
	const [hasMediaPermission, setHasMediaPermission] = useState(false);
	const [connectionStatus, setConnectionStatus] = useState('OFFLINE');

	// Managers
	const webrtcManager = WebRTCManager.getInstance();
	const socketManager = SocketManager.getInstance();

	// Initialize socket connection and event listeners
	useEffect(() => {
		const initializeSocket = async () => {
			try {
				const socket = await socketManager.connect();
				if (socket) {
					console.log('Socket connected successfully');
					setConnectionStatus('CONNECTED');

					// Register event listeners after connection is established
					socketManager.onConnect(() => {
						console.log('Connected to socket');
						setConnectionStatus('CONNECTED');
					});

					socketManager.onDisconnect(() => {
						console.log('Disconnected from socket');
						setConnectionStatus('OFFLINE');
					});

					socketManager.onError((error) => {
						console.error('Socket error:', error);
						setConnectionStatus('OFFLINE');
					});

					// Conference events
					socketManager.onConferenceRoomCreated((data) => {
						console.log('Conference room created:', data);
						setConferenceState((prev) => ({
							...prev,
							roomId: data.roomId,
							isHost: true,
							isInConference: true,
						}));
					});

					socketManager.onConferenceJoined((data) => {
						console.log('Joined conference:', data);
						setConferenceState((prev) => ({
							...prev,
							roomId: data.roomId,
							isInConference: true,
							isHost: data.yourRole === 'host',
							participants: data.participants || [],
							roomSettings: {
								...prev.roomSettings,
								...data.roomSettings,
							},
						}));
					});

					socketManager.onParticipantJoined((data) => {
						console.log('Participant joined:', data);
						// Backend sends: { participant: { userId, displayName, isGuest, joinedAt }, roomId, totalParticipants }
						const participant = data.participant;
						if (participant) {
							setConferenceState((prev) => ({
								...prev,
								participants: [
									...prev.participants,
									{
										userId: participant.userId,
										displayName:
											participant.displayName ||
											'Anonymous',
										joinedAt: participant.joinedAt,
										isGuest: participant.isGuest,
										role: participant.role || 'participant',
										mediaState: participant.mediaState || {
											video: false,
											audio: false,
										},
									},
								],
							}));
						}
					});

					socketManager.onParticipantLeft((data) => {
						console.log('Participant left:', data);
						// Backend sends: { userId: "guest_xxx", roomId: "room_xxx" }
						const userId = data.userId || data.participantId; // Support both formats
						if (userId) {
							setConferenceState((prev) => ({
								...prev,
								participants: prev.participants.filter(
									(p) => p.userId !== userId
								),
							}));
						}
					});

					socketManager.onConferenceError((error) => {
						console.error('Conference error:', error);
						setConnectionStatus('OFFLINE');
						// Handle conference errors appropriately
					});

					// WebRTC signaling events - these will be handled by Meet.web.tsx
					// We just register them here so they're available
					console.log(
						'🔗 Registering WebRTC signaling event handlers...'
					);

					// Note: The actual handlers will be set by Meet.web.tsx
					// This just ensures the events are available through SocketManager

					// Chat events - Fixed message format handling
					socketManager.onMeetingMessage((message) => {
						console.log('Received meeting message:', message);
						// Transform backend message format to frontend format
						const transformedMessage = {
							_id: message.messageId || message._id,
							text: message.text || message.message,
							user: {
								_id: message.user?._id,
								displayName:
									message.user?.displayName || 'Anonymous',
							},
							createdAt:
								message.createdAt ||
								message.timestamp ||
								new Date().toISOString(),
						};
						setChatState((prev) => ({
							...prev,
							messages: [...prev.messages, transformedMessage],
						}));
					});

					socketManager.onChatHistory((data) => {
						console.log('Received chat history:', data);
						if (data.messages && data.messages.length > 0) {
							// Transform all messages to frontend format
							const transformedMessages = data.messages.map(
								(message) => ({
									_id: message.messageId || message._id,
									text: message.text || message.message,
									user: {
										_id: message.user?._id,
										displayName:
											message.user?.displayName ||
											'Anonymous',
									},
									createdAt:
										message.createdAt ||
										message.timestamp ||
										new Date().toISOString(),
								})
							);
							setChatState((prev) => ({
								...prev,
								messages: [
									...prev.messages,
									...transformedMessages,
								],
							}));
						}
					});

					socketManager.onMessageSent((data) => {
						console.log('Message sent acknowledgment:', data);
						// Message was successfully sent and saved to database
					});

					socketManager.onChatError((error) => {
						console.error('Chat error:', error);
						// Handle chat errors appropriately
					});

					socketManager.onUserTyping((data) => {
						console.log('User typing:', data);
						setChatState((prev) => ({
							...prev,
							typingUsers: prev.typingUsers
								.filter((u) => u.user._id !== data.user._id)
								.concat(data.isTyping ? [data] : []),
						}));
					});
				}
			} catch (error) {
				console.error('Failed to initialize socket:', error);
				setConnectionStatus('OFFLINE');
			}
		};

		initializeSocket();

		return () => {
			socketManager.disconnect();
		};
	}, []);

	// Meeting functions
	const createConference = useCallback(
		async (meetingName = 'Quick Meeting') => {
			try {
				setIsLoading(true);

				if (!hasMediaPermission) {
					const granted =
						await webrtcManager.requestMediaPermissions();
					if (!granted) {
						Alert.alert(
							'Permissions Required',
							'Camera and microphone access are required to create a meeting.'
						);
						setIsLoading(false);
						return;
					}
					setHasMediaPermission(true);
				}

				const response = await publicApi.post('/meeting/create', {
					title: meetingName,
					description: `Meeting created by user`,
					maxParticipants:
						conferenceState.roomSettings.maxParticipants,
					roomType: 'meeting',
					allowScreenShare:
						conferenceState.roomSettings.allowScreenShare,
					allowChat: conferenceState.roomSettings.allowChat,
					allowRecording: true,
				});

				if (response.data.success) {
					setConferenceState((prev) => ({
						...prev,
						roomId: response.data.data.roomId,
						isHost: true,
						isInConference: true,
					}));

					// Initialize local media for video calling
					try {
						await getLocalMedia(true);
						console.log(
							'Local media initialized for meeting creation'
						);
					} catch (error) {
						console.warn(
							'Failed to initialize local media:',
							error
						);
						// Don't fail the meeting creation if media fails
					}

					Alert.alert(
						'Meeting Created Successfully! 🎉',
						`Meeting "${meetingName}" has been created!\nRoom ID: ${response.data.data.roomId}\n\nClick the camera button to start your video feed.`
					);
				} else {
					throw new Error(
						response.data.message || 'Conference creation failed'
					);
				}
			} catch (error) {
				console.error('Error creating conference:', error);
				Alert.alert(
					'Error',
					`Failed to create conference room.\n\nError: ${error.message || 'Unknown error'}`
				);
			} finally {
				setIsLoading(false);
			}
		},
		[conferenceState.roomSettings, hasMediaPermission]
	);

	const joinConference = useCallback(async (roomId: string) => {
		try {
			setIsLoading(true);

			const response = await publicApi.get(`/meeting/${roomId}`);

			if (response.data.success) {
				const meetingData = response.data.data;
				setConferenceState((prev) => ({
					...prev,
					roomId: meetingData.roomId,
					isInConference: true,
					isHost: false,
					participants: [],
					roomSettings: {
						maxParticipants: meetingData.maxParticipants,
						isLocked: meetingData.isLocked,
						allowScreenShare: meetingData.settings.allowScreenShare,
						allowChat: meetingData.settings.allowChat,
					},
				}));

				// Initialize local media for video calling
				try {
					await getLocalMedia(true);
					console.log('Local media initialized for meeting join');
				} catch (error) {
					console.warn('Failed to initialize local media:', error);
					// Don't fail the meeting join if media fails
				}

				Alert.alert(
					'Success! 🎉',
					`Successfully joined meeting: ${meetingData.title}\nRoom ID: ${roomId}\n\nClick the camera button to start your video feed.`
				);
			} else {
				throw new Error(
					response.data.error || 'Failed to join meeting'
				);
			}
		} catch (error) {
			console.error('Error joining conference:', error);
			Alert.alert(
				'Error',
				`Failed to join conference room.\n\nError: ${error.message || 'Unknown error'}`
			);
		} finally {
			setIsLoading(false);
		}
	}, []);

	const leaveConference = useCallback(() => {
		// Notify backend that user is leaving
		if (conferenceState.roomId) {
			socketManager.leaveConference(conferenceState.roomId);
		}

		setConferenceState((prev) => ({
			...prev,
			isInConference: false,
			roomId: null,
			isHost: false,
			participants: [],
		}));

		// Clear chat messages when leaving
		setChatState((prev) => ({
			...prev,
			messages: [],
			newMessage: '',
			typingUsers: [],
		}));

		cleanupMedia();
		console.log('Left conference and cleaned up media');
	}, [conferenceState.roomId]);

	// Chat functions
	const joinMeetingRoom = useCallback(
		(userId: string) => {
			if (conferenceState.roomId) {
				console.log(
					'Joining conference room:',
					conferenceState.roomId,
					'with user:',
					userId
				);

				// Join the conference room - this will trigger participant_joined events for others
				socketManager.joinConference({
					roomId: conferenceState.roomId,
					userId: userId,
					displayName: 'User',
					isGuest: false,
				});

				// Join the meeting chat
				socketManager.joinMeetingChat(
					conferenceState.roomId,
					conferenceState.roomId,
					{
						_id: userId,
						displayName: 'User',
						isGuest: false,
					}
				);

				// Request chat history after a short delay
				setTimeout(() => {
					socketManager.getChatHistory(
						conferenceState.roomId!,
						conferenceState.roomId!,
						50
					);
				}, 1500);
			}
		},
		[conferenceState.roomId]
	);

	const sendMessage = useCallback(
		(userId: string) => {
			if (chatState.newMessage.trim() && conferenceState.roomId) {
				socketManager.sendMeetingMessage({
					meetingId: conferenceState.roomId,
					roomId: conferenceState.roomId,
					user: {
						_id: userId,
						displayName: 'User',
						isGuest: false,
					},
					text: chatState.newMessage.trim(), // Use 'text' field as backend expects
					message: chatState.newMessage.trim(), // Also include 'message' for backward compatibility
				});

				setChatState((prev) => ({
					...prev,
					newMessage: '',
				}));
			}
		},
		[chatState.newMessage, conferenceState.roomId]
	);

	const handleTyping = useCallback(
		(isTyping: boolean, userId: string) => {
			if (conferenceState.roomId) {
				socketManager.sendUserTyping({
					meetingId: conferenceState.roomId,
					roomId: conferenceState.roomId,
					user: {
						_id: userId,
						displayName: 'User',
						isGuest: false,
					},
					isTyping,
				});
			}
		},
		[conferenceState.roomId]
	);

	const loadChatHistory = useCallback(() => {
		if (conferenceState.roomId) {
			socketManager.getChatHistory(
				conferenceState.roomId,
				conferenceState.roomId,
				50
			);
		}
	}, [conferenceState.roomId]);

	// Media functions
	const getLocalMedia = useCallback(
		async (video: boolean = true): Promise<MediaStream | null> => {
			try {
				const stream = await webrtcManager.getLocalMedia(video);
				setHasMediaPermission(true);
				return stream;
			} catch (error) {
				console.error('Error accessing media devices:', error);
				setHasMediaPermission(false);
				throw error;
			}
		},
		[]
	);

	const cleanupMedia = useCallback(() => {
		webrtcManager.cleanup();
	}, []);

	const initializePeerConnection = useCallback(
		async (callId: string): Promise<RTCPeerConnection> => {
			return await webrtcManager.initializePeerConnection(callId);
		},
		[]
	);

	return {
		// States
		callState,
		mediaState,
		conferenceState,
		chatState,
		isLoading,
		hasMediaPermission,
		connectionStatus,
		webrtcManager,
		socketManager,

		// State setters
		setMediaState,
		setHasMediaPermission,
		setChatState,

		// Meeting functions
		createConference,
		joinConference,
		leaveConference,

		// Chat functions
		joinMeetingRoom,
		sendMessage,
		handleTyping,
		loadChatHistory,

		// Media functions
		getLocalMedia,
		cleanupMedia,
		initializePeerConnection,

		// Data collection functions
		requestDataCollection: (dataItem: any) => {
			if (!conferenceState.roomId || !socketManager) {
				console.error(
					'Cannot request data collection: missing room or socket'
				);
				return;
			}

      console.log("data item", dataItem);

			const requestData = {
				roomId: conferenceState.roomId,
				dataId: dataItem._id,
				dataTitle: dataItem.dataTitle,
				dataType: dataItem.dataType,
				schema: dataItem.dataSchema, // Assuming schema is part of dataItem
				requestedBy: {
					userId: callState.userId || 'unknown',
					displayName: callState.displayName || 'Unknown User',
				},
			};

			socketManager.requestDataCollection(requestData);
		},

		onDataFormRequested: (callback: (data: any) => void) => {
			if (socketManager) {
				socketManager.onDataFormRequested(callback);
			}
		},

		submitDataResponse: (dataId: string, formData: any) => {
			if (!conferenceState.roomId || !socketManager) {
				console.error(
					'Cannot submit data response: missing room or socket'
				);
				return;
			}

			const responseData = {
				roomId: conferenceState.roomId,
				dataId: dataId,
				responseId: `response_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
				formData: formData,
				respondent: {
					userId: callState.userId || 'unknown',
					displayName: callState.displayName || 'Unknown User',
				},
			};

			socketManager.submitDataCollectionResponse(responseData);
		},

		onDataCollectionResponse: (callback: (data: any) => void) => {
			if (socketManager) {
				socketManager.onDataCollectionResponse(callback);
			}
		},

		onDataCollectionComplete: (callback: (data: any) => void) => {
			if (socketManager) {
				socketManager.onDataCollectionComplete(callback);
			}
		},
	};
};

export default useMeeting;
